// Runs the automatic quality check (F-10, D-55) for one B: first the frame check (drops / inserts / duplicates,
// F-03.3), then every A frame against the B frame shown with it (src/workers/quality-worker.ts). Results are
// navigation hints only (D-07): suspicious segments on the timeline and in the 檢查 tab.

import type { App } from '../app/app';
import type { SourceId } from '../core/types';
import { Feeder } from '../media/feeder';
import { tickOfAFrame } from '../core/timeline';
import { GRID_X, GRID_Y, framePoints, packPoints, segments, unpackPoints, type FrameEvent, type FramePoints, type FrameStats, type Finding, type QualityCat, type Sensitivity, type StoredQuality } from '../core/quality';
import type { Project } from '../core/types';
import type { StoredAiIssue } from '../core/quality';
import { kvGet } from '../app/persist';
import { AI_ANALYSIS_ENABLED } from '../ai/enabled';
import type { TemporalManager } from './temporal-manager';
import type { FaceStat } from '../core/face';

type FaceMsg = { id?: number; type?: string; face?: FaceStat | null; faces?: number; ms?: number; error?: string };

/** Long edge of the analysis picture (docs/analysis-modules.md "分析解析度策略"). */
const ANALYSIS_W = 960;

export interface QualityResult {
  source: SourceId;
  /** What was checked; results no longer match when it changes. */
  key: string;
  first: number;
  /** Number of A frames covered. */
  n: number;
  checkedAt: string;
  pts: FramePoints;
  events: FrameEvent[];
  ai: StoredAiIssue[];
  /** Compared area in A pixels (for placing the crosshair on a finding). */
  area: { x: number; y: number; w: number; h: number };
  /** Time offset suggested by the frame check when it differs from the one in use. */
  offsetHint: number | null;
  /** The frame check ran (only one-to-one versions have it). */
  framesChecked: boolean;
  findings: Finding[];
  curves: Record<QualityCat, Float32Array>;
  sensitivity: Sensitivity;
}

export class QualityManager {
  running: SourceId | null = null;
  /** Whole-project check in progress: shot number and count (F-10.8). */
  batch: { shot: number; shots: number } | null = null;
  private batchCancelled = false;
  /** Results derived from the stored checks, per stored object and sensitivity. */
  private cache = new WeakMap<StoredQuality, QualityResult>();
  phase: 'frames' | 'pixels' = 'pixels';
  progress = 0;
  error: string | null = null;
  /** The face check could not start on this computer (F-10.11); the other checks still ran. */
  faceError: string | null = null;
  /** Face check speed of the last run (ms per frame pair), for the spike record (S-13). */
  faceMs = 0;
  sensitivity: Sensitivity = 'medium';
  onChange: (() => void) | null = null;
  private cancelled = false;

  constructor(private app: App) {}

  private get temporal(): TemporalManager | undefined {
    return (this.app as unknown as { temporal?: TemporalManager }).temporal;
  }

  /** Identity of what a check covers: files, time and spatial alignment, range. */
  keyFor(id: SourceId): string {
    const a = this.app.sources.source('A');
    const b = this.app.sources.source(id);
    if (!a || !b) return '';
    const pb = this.app.project.playback;
    return JSON.stringify([a.file.fingerprint, b.file.fingerprint, b.frameOffset, b.frameMapping, b.spatialTransform, pb.inPoint, pb.outPoint]);
  }

  /** Whether the stored result for a source still matches the current sources and settings. */
  current(id: SourceId): boolean {
    const r = this.results.get(id);
    return !!r && r.key === this.keyFor(id);
  }

  cancel(): void {
    this.aiAbort?.abort();
    this.cancelled = true;
    this.batchCancelled = true;
    this.temporal?.cancel();
  }

  setSensitivity(s: Sensitivity): void {
    this.sensitivity = s;
    this.onChange?.();
  }

  /** The check of a version in a shot's project (the current shot when omitted), at the current sensitivity. */
  resultFor(id: SourceId, project: Project = this.app.project): QualityResult | undefined {
    const st = project.qualityChecks?.[id];
    if (!st) return undefined;
    let r = this.cache.get(st);
    if (!r) {
      r = {
        source: id,
        key: st.key,
        first: st.first,
        n: st.n,
        checkedAt: st.checkedAt,
        pts: unpackPoints(st.points, st.n),
        events: st.events,
        ai: st.ai ?? [],
        area: st.area,
        offsetHint: st.offsetHint,
        framesChecked: st.framesChecked,
        findings: [],
        curves: {} as QualityResult['curves'],
        sensitivity: this.sensitivity,
      };
      this.evaluate(r);
      this.cache.set(st, r);
    }
    if (r.sensitivity !== this.sensitivity) this.evaluate(r);
    return r;
  }

  /** Results of the current shot's loaded versions whose files still match. */
  get results(): Map<SourceId, QualityResult> {
    const out = new Map<SourceId, QualityResult>();
    const a = this.app.sources.source('A');
    for (const id of this.app.sources.loadedBs()) {
      const r = this.resultFor(id);
      const b = this.app.sources.source(id);
      if (!r || !a || !b) continue;
      const k = JSON.parse(r.key) as string[];
      if (k[0] === a.file.fingerprint && k[1] === b.file.fingerprint) out.set(id, r);
    }
    return out;
  }

  /** Number of findings in a shot's checks (for the project tree), or null when nothing was checked. */
  countFor(project: Project): number | null {
    const ids = Object.keys(project.qualityChecks ?? {}) as SourceId[];
    if (!ids.length) return null;
    return ids.reduce((n, id) => n + (this.resultFor(id, project)?.findings.length ?? 0), 0);
  }

  private evaluate(r: QualityResult): void {
    const f = segments(r.pts, r.n, this.sensitivity, r.events, r.first);
    // The AI look's problems are listed as they are (no threshold); low sensitivity hides the minor ones.
    const minSev = this.sensitivity === 'low' ? 2 : 1;
    for (const a of r.ai)
      if (a.severity >= minSev)
        f.findings.push({ cat: 'ai', start: a.frame, end: a.frame, peak: a.frame, score: a.severity, cell: null, kind: a.type, value: a.severity, text: a.text, box: a.box });
    f.findings.sort((x, y) => x.start - y.start);
    r.findings = f.findings;
    r.curves = f.curves;
    r.sensitivity = this.sensitivity;
  }

  /** AI look in progress: frames done and total (F-10.10). */
  ai: { done: number; total: number } | null = null;
  private aiAbort: AbortController | null = null;

  /**
   * AI look (F-10.10, D-57): sends A and B at up to 8 frames — the check's most suspicious frames first — to the
   * Claude API with the user's key and keeps the problems it reports with the version's check.
   */
  async aiLook(id: SourceId): Promise<number> {
    const app = this.app;
    if (!AI_ANALYSIS_ENABLED) return 0;
    const key = await kvGet<string>('aiKey');
    if (!key) throw new Error('ai.error.noKey');
    if (this.running || this.ai) return 0;
    const model = ((await kvGet<string>('aiModel')) ?? 'claude-sonnet-5-5') as import('../ai/vision').AiModel;
    const { lookAtFrames, pickFrames } = await import('../ai/vision');
    const cur = this.current(id) ? this.resultFor(id) : undefined;
    const na = app.sources.source('A')!.spec.frameCount;
    const tl = app.sources.timeline;
    const pb = app.project.playback;
    const first = pb.inPoint !== undefined ? Math.floor(pb.inPoint / tl.ticksPerFrame) : 0;
    const last = pb.outPoint !== undefined ? Math.min(na - 1, Math.floor(pb.outPoint / tl.ticksPerFrame)) : na - 1;
    const peaks = (cur?.findings ?? []).filter((f) => f.cat !== 'ai').map((f) => ({ frame: f.peak, score: f.score }));
    const frames = pickFrames(peaks, first, last, 8);
    this.ai = { done: 0, total: frames.length };
    this.error = null;
    this.aiAbort = new AbortController();
    this.onChange?.();
    try {
      const issues = await lookAtFrames(app, id, frames, {
        key,
        model,
        brief: app.project.aiBrief ?? '',
        lang: app.state.settings.language,
        signal: this.aiAbort.signal,
        onProgress: (d, n) => {
          this.ai = { done: d, total: n };
          this.onChange?.();
        },
      });
      const keyNow = this.keyFor(id);
      const base: StoredQuality =
        app.project.qualityChecks?.[id]?.key === keyNow
          ? app.project.qualityChecks[id]!
          : { key: keyNow, checkedAt: new Date().toISOString(), first, n: 0, events: [], area: { x: 0, y: 0, w: app.sources.source('A')!.spec.width, h: app.sources.source('A')!.spec.height }, offsetHint: null, framesChecked: false, points: {} };
      const st: StoredQuality = { ...base, ai: issues };
      app.store.project((p) => (p.qualityChecks = { ...(p.qualityChecks ?? {}), [id]: st }), { undo: false });
      return issues.length;
    } finally {
      this.ai = null;
      this.aiAbort = null;
      this.onChange?.();
    }
  }

  /**
   * Checks every version of every shot of the open project that has no up-to-date check (F-10.8): each shot is
   * opened in turn; shots whose videos cannot be opened without asking are skipped. Returns to the starting shot.
   */
  async checkProject(): Promise<{ checked: number; skipped: number }> {
    const ws = this.app.ws;
    const wp = ws.activeProject();
    const start = ws.activeShot()?.id;
    let checked = 0;
    let skipped = 0;
    if (!wp || this.running || this.batch) return { checked, skipped };
    this.batchCancelled = false;
    try {
      for (let i = 0; i < wp.shots.length && !this.batchCancelled; i++) {
        const sh = wp.shots[i];
        this.batch = { shot: i + 1, shots: wp.shots.length };
        this.onChange?.();
        if (!sh.project.sources.length) continue;
        await ws.activate(wp.id, sh.id);
        const p = this.app.project;
        if (!this.app.sources.has('A') || p.sources.some((s) => !this.app.sources.has(s.id))) {
          skipped++;
          continue;
        }
        for (const id of this.app.sources.loadedBs()) {
          if (this.batchCancelled) break;
          if (this.current(id)) continue;
          if (await this.check(id)) checked++;
        }
      }
    } finally {
      this.batch = null;
      if (start && ws.activeShot()?.id !== start) await ws.activate(wp.id, start);
      this.onChange?.();
    }
    return { checked, skipped };
  }

  async check(id: SourceId): Promise<QualityResult | null> {
    const app = this.app;
    const a = app.sources.source('A');
    const b = app.sources.source(id);
    if (!a || !b || this.running || id === 'A' || !app.sources.has('A') || !app.sources.has(id)) return null;
    this.running = id;
    this.cancelled = false;
    this.error = null;
    this.progress = 0;
    this.onChange?.();
    const key = this.keyFor(id);
    try {
      // 1. Frame check (drops, inserts, duplicates) on one-to-one versions (F-03.3).
      let events: FrameEvent[] = [];
      let offsetHint: number | null = null;
      let framesChecked = false;
      const tm = this.temporal;
      if (tm && b.frameMapping.mode === 'one-to-one') {
        this.phase = 'frames';
        const prev = tm.onChange;
        tm.onChange = () => {
          this.progress = tm.progress;
          this.onChange?.();
          prev?.();
        };
        try {
          const t = await tm.analyse(id);
          if (t) {
            framesChecked = true;
            events = t.events.map((e) => ({ kind: e.kind, aFrame: e.aFrame, bFrame: e.bFrame, count: e.count }));
            if (t.offset && t.offset.offset !== b.frameOffset && t.offset.confidence >= 1.3) offsetHint = t.offset.offset;
          }
        } finally {
          tm.onChange = prev;
        }
        if (this.cancelled) return null;
      }
      // 2. Every A frame against the B frame shown with it.
      this.phase = 'pixels';
      this.progress = 0;
      this.onChange?.();
      const st = await this.pixels(key, id, events, offsetHint, framesChecked);
      if (!st) return null;
      // Kept with the shot (saved in the project file, F-10.7); not an undoable edit. An AI look of the same
      // material is kept.
      const old = this.app.project.qualityChecks?.[id];
      if (old?.ai && old.key === st.key) st.ai = old.ai;
      this.app.store.project((p) => (p.qualityChecks = { ...(p.qualityChecks ?? {}), [id]: st }), { undo: false });
      return this.resultFor(id) ?? null;
    } catch (e) {
      if (!this.cancelled) this.error = e instanceof Error ? e.message : String(e);
      return null;
    } finally {
      this.running = null;
      this.progress = 1;
      this.onChange?.();
    }
  }

  private async pixels(key: string, id: SourceId, events: FrameEvent[], offsetHint: number | null, framesChecked: boolean): Promise<StoredQuality | null> {
    const app = this.app;
    const a = app.sources.source('A')!;
    const b = app.sources.source(id)!;
    const tl = app.sources.timeline;
    const pb = app.project.playback;
    const na = a.spec.frameCount;
    const first = pb.inPoint !== undefined ? Math.max(0, Math.floor(pb.inPoint / tl.ticksPerFrame)) : 0;
    const last = pb.outPoint !== undefined ? Math.min(na - 1, Math.floor(pb.outPoint / tl.ticksPerFrame)) : na - 1;
    // Analysis picture: A scaled so its long edge is at most ANALYSIS_W; B placed by its spatial transform (F-03).
    const k = Math.min(1, ANALYSIS_W / Math.max(a.spec.width, a.spec.height));
    const w = Math.max(16, Math.round(a.spec.width * k));
    const h = Math.max(16, Math.round(a.spec.height * k));
    const st = b.spatialTransform;
    const bRect: [number, number, number, number] = [st.dx * k, st.dy * k, b.spec.width * st.sx * k, b.spec.height * st.sy * k];
    const x0 = Math.ceil(Math.max(0, bRect[0]) + 2);
    const y0 = Math.ceil(Math.max(0, bRect[1]) + 2);
    const x1 = Math.floor(Math.min(w, bRect[0] + bRect[2]) - 2);
    const y1 = Math.floor(Math.min(h, bRect[1] + bRect[3]) - 2);
    if (x1 - x0 < 48 || y1 - y0 < 32) throw new Error('B does not overlap A');
    const crop: [number, number, number, number] = [x0, y0, x1 - x0, y1 - y0];
    const worker = new Worker(new URL('../workers/quality-worker.ts', import.meta.url), { type: 'module' });
    worker.postMessage({ type: 'init', w, h, bRect, crop });
    // F-10.11: faces on their own worker, fed the same frames (clones) and placed the same way.
    const faceStats: (FrameStats['face'] | undefined)[] = new Array(last - first + 1);
    const facePending = new Map<number, (m: FaceMsg) => void>();
    let faceMsSum = 0;
    let faceN = 0;
    const faceWorker = new Worker(new URL('../workers/face-worker.ts', import.meta.url), { type: 'module' });
    const faceReady = new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => resolve(false), 30000);
      faceWorker.onmessage = (ev: MessageEvent<FaceMsg>) => {
        if (ev.data.type === 'ready') {
          clearTimeout(timer);
          resolve(true);
        } else if (ev.data.type === 'error') {
          clearTimeout(timer);
          this.faceError = ev.data.error ?? 'face';
          resolve(false);
        } else if (ev.data.id !== undefined) {
          facePending.get(ev.data.id)?.(ev.data);
          facePending.delete(ev.data.id);
        }
      };
      faceWorker.onerror = (e) => {
        clearTimeout(timer);
        this.faceError = e.message || 'face worker';
        resolve(false);
      };
    });
    this.faceError = null;
    faceWorker.postMessage({ type: 'init', base: new URL('./', document.baseURI).href, w, h, bRect });
    let faceOn: boolean;
    const fa = new Feeder(app.sources.runtime.get('A')!.client.media);
    const fb = new Feeder(app.sources.runtime.get(id)!.client.media);
    const stats: (FrameStats | null)[] = new Array(last - first + 1).fill(null);
    const pending = new Map<number, (m: { stats?: FrameStats; error?: string }) => void>();
    worker.onmessage = (ev: MessageEvent<{ id: number; stats?: FrameStats; error?: string }>) => {
      pending.get(ev.data.id)?.(ev.data);
      pending.delete(ev.data.id);
    };
    const bFrameOf = (i: number): number | null => {
      const at = app.sources.frameAt(id, tickOfAFrame(tl, i));
      return !at || at.held || at.frame === null ? null : at.frame;
    };
    let lastB: { j: number; vf: VideoFrame } | null = null;
    const inFlight: Promise<void>[] = [];
    try {
      await Promise.all([fa.open(), fb.open()]);
      faceOn = await faceReady;
      let prevJ: number | null = null;
      for (let i = first; i <= last; i++) {
        if (this.cancelled) return null;
        const j = bFrameOf(i);
        if (j === null) {
          prevJ = null;
          continue;
        }
        const aheadA = [1, 2, 3, 4, 5, 6].map((d) => i + d).filter((x) => x <= last);
        const aheadB = aheadA.map(bFrameOf).filter((x): x is number => x !== null && x !== j);
        const cancelled = () => this.cancelled;
        await fa.get(i, aheadA, cancelled);
        const va = fa.take(i);
        let vb: VideoFrame | undefined;
        if (lastB && lastB.j === j) vb = lastB.vf.clone();
        else {
          await fb.get(j, aheadB, cancelled);
          vb = fb.take(j);
          if (vb) {
            lastB?.vf.close();
            lastB = { j, vf: vb.clone() };
          }
        }
        if (!va || !vb) {
          va?.close();
          vb?.close();
          prevJ = null;
          continue;
        }
        const sameB = prevJ === j;
        prevJ = j;
        const msgId = i;
        inFlight.push(
          new Promise<void>((resolve, reject) => {
            pending.set(msgId, (m) => {
              if (m.error) reject(new Error(m.error));
              else {
                stats[msgId - first] = m.stats ?? null;
                resolve();
              }
            });
          }),
        );
        if (faceOn) {
          const ca = va.clone();
          const cb = vb.clone();
          inFlight.push(
            new Promise<void>((resolve) => {
              facePending.set(msgId, (m) => {
                if (m.face) {
                  // Analysis-picture pixels → A pixels; the cell is within the compared area (crop).
                  const at = { x: m.face.cx / k, y: m.face.cy / k };
                  const cx = Math.floor(((m.face.cx - crop[0]) / crop[2]) * GRID_X);
                  const cy = Math.floor(((m.face.cy - crop[1]) / crop[3]) * GRID_Y);
                  const cell = cx >= 0 && cy >= 0 && cx < GRID_X && cy < GRID_Y ? cy * GRID_X + cx : null;
                  faceStats[msgId - first] = { ...m.face, at, cell };
                } else faceStats[msgId - first] = null;
                if (m.ms !== undefined) {
                  faceMsSum += m.ms;
                  faceN++;
                }
                resolve();
              });
            }),
          );
          faceWorker.postMessage({ type: 'pair', id: msgId, a: ca, b: cb, ts: Math.round((msgId * 1000) / Math.max(1, a.spec.frameRate)) }, [ca, cb]);
        }
        worker.postMessage({ type: 'pair', id: msgId, a: va, b: vb, sameB }, [va, vb]);
        // Decode the next pair while the worker measures this one (at most 2 pairs queued).
        if (inFlight.length >= 2) await inFlight.shift();
        const done = i - first + 1;
        if (done % 4 === 0) {
          this.progress = done / (last - first + 1);
          this.onChange?.();
        }
      }
      await Promise.all(inFlight);
    } finally {
      lastB?.vf.close();
      fa.dispose();
      fb.dispose();
      worker.terminate();
      faceWorker.terminate();
    }
    this.faceMs = faceN ? faceMsSum / faceN : 0;
    const r = (v: number) => Math.round(v * 10) / 10;
    return {
      key,
      checkedAt: new Date().toISOString(),
      first,
      n: stats.length,
      events: events.filter((e) => e.aFrame >= first && e.aFrame <= last),
      area: { x: r(crop[0] / k), y: r(crop[1] / k), w: r(crop[2] / k), h: r(crop[3] / k) },
      offsetHint,
      framesChecked,
      points: packPoints(framePoints(stats.map((s, i) => (s && faceStats[i] !== undefined ? { ...s, face: faceStats[i] } : s)))),
    };
  }
}
